/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React from "react";
import { useSelector } from "react-redux";
import { setScheduledTask } from "../../../store/api/tasks/reducer";
import { tasksSelector } from "../../../store/api/tasks/selector";
import { setErrorMsg } from "../../../store/helpers/helperReducers";
import { errorMsgSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { rnLedActions } from "../../../globals/constants";
import { RadixSelectControl } from "../../ui/radix-select";
import { Label } from "../../ui/label";
import { InputField } from "../../Shared/Radix/Inputs/InputField";

export const RadioNodeOperations = () => {
    const dispatch = useStoreDispatch();
    const errorMsg = useSelector(errorMsgSelector);
    const scheduledtasks = useSelector(tasksSelector);

    const onInputChange = (name: string, value: string) => {
        dispatch(setScheduledTask({ [name]: value }));
        dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
    };

    const selectChangeHandler = (name: string) => (value: string) => {
        dispatch(setScheduledTask({ [name]: value }));
        if (name === "actionName") {
            dispatch(setErrorMsg({ ...errorMsg, [name]: "", followIMSI: "", radioNodeId: "" }));
        } else {
            dispatch(setErrorMsg({ ...errorMsg, [name]: "" }));
        }
    };

    const onBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === "radioNodeId") {
            if (!value) {
                dispatch(setErrorMsg({ ...errorMsg, followIMSI: "", [name]: "Node ID is required!" }));
            } else if (Number(value) <= 0 || Number(value) >= 1000) {
                dispatch(setErrorMsg({ ...errorMsg, followIMSI: "", [name]: "Invalid input! Please enter a numeric value greater than 0 and up to 3 digits." }));
            }
        }
        if (name === "followIMSI") {
            if (!value) {
                dispatch(setErrorMsg({ ...errorMsg, radioNodeId: "", [name]: "IMSI is required!" }));
            }
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <p className="text-sm font-medium">Select an operation on the radio node:</p>
            <div className="space-y-2">
                <Label>Type</Label>
                <RadixSelectControl
                    options={[{ value: "LED Operations", label: "LED Operations" }]}
                    value={scheduledtasks.idType || "LED Operations"}
                    placeholder="__placeholder__"
                    onValueChange={selectChangeHandler("idType")}
                    disabled={true}
                />
            </div>
            <div className="space-y-2">
                <Label>Action</Label>
                <RadixSelectControl
                    options={rnLedActions.map((item: string) => ({ value: item, label: item }))}
                    value={scheduledtasks.actionName}
                    placeholder="__placeholder__"
                    onValueChange={selectChangeHandler("actionName")}
                />
            </div>
            {(scheduledtasks?.actionName === rnLedActions[0] || !scheduledtasks?.actionName) && (
                <div>
                    <InputField
                        label="Node ID"
                        name="radioNodeId"
                        value={scheduledtasks?.radioNodeId ?? ""}
                        type="number"
                        required
                        min={1}
                        max={999}
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter node id"
                    />
                </div>
            )}
            {scheduledtasks?.actionName === rnLedActions[1] && (
                <div>
                    <InputField
                        label="IMSI"
                        name="followIMSI"
                        value={scheduledtasks?.followIMSI ?? ""}
                        required
                        onChange={onInputChange}
                        onBlur={onBlurHandler}
                        placeholder="Enter imsi"
                    />
                </div>
            )}
        </div>
    );
};